import { EditOutlined, DeleteOutlined } from "@ant-design/icons"
import type { TableProps } from "antd"
import { Button, Space, Table, Tag, Tooltip, Popconfirm } from "antd"
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"
import type { Article } from "@/services/article/articleApi"

interface ArticleTableProps {
  data: Article[]
  loading: boolean
  onEdit: (record: Article) => void
  onDelete: (id: number) => void
  pagination?: TableProps["pagination"]
}

const ArticleTable: React.FC<ArticleTableProps> = ({ data, loading, onEdit, onDelete, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Tiêu đề bài viết",
      dataIndex: "title",
      key: "title",
      width: 250,
      fixed: "left",
      ellipsis: { showTitle: true },
      render: (text: string) => <span className="font-semibold text-gray-900">{text}</span>,
    },
    {
      title: "Loại bài viết",
      dataIndex: "type",
      key: "type",
      width: 130,
      align: "center",
      render: (type: string) => {
        if (type === "notification") {
          return <Tag color="blue">Thông báo</Tag>
        }
        return <Tag color="default">{type}</Tag>
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status: string) => {
        if (status === "published") {
          return <Tag color="success">Xuất bản</Tag>
        }
        return <Tag color="warning">Bản nháp</Tag>
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value: string) => (value ? formatDateTime(value, "dd/mm/yy hh:mm") : "—"),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record: any) => (
        <Space size="small" wrap={false}>
          <Tooltip title="Sửa bài viết">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-base! text-blue-500! hover:text-blue-600!" />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Bạn có chắc muốn xóa bài viết này không?"
            onConfirm={() => onDelete(record.id)}
            okText="Có, xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Xóa bài viết">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined className="text-base! text-red-500! hover:text-red-600!" />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

ArticleTable.displayName = "ArticleTable"

export default ArticleTable
